import { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import paymentServices from '../../services/paymentServices';
import { AuthContext } from '../../context/AuthProvider';

const modal_style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 640 },
  maxHeight: '92vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 3 },
};

const STATUS_COLORS = {
  sent: 'info',
  paid: 'success',
  partially_paid: 'warning',
  replaced: 'default',
  canceled: 'default',
  cancellation_pending: 'error',
  error: 'error',
  delivery_failed: 'error',
};

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const methodsLabel = (m) => {
  const parts = [];
  if (m?.ach && m?.card) parts.push('ACH + Card');
  else if (m?.ach) parts.push('ACH');
  else if (m?.card) parts.push('Card');
  if (m?.cash) parts.push('Cash');
  if (m?.check) parts.push('Check');
  return parts.join(', ') || '—';
};

/**
 * "Send Invoice" modal (replaces the old Amount Due reminder button).
 * Sends the customer a payment request: Square online payment (ACH and/or
 * card, hosted by Square) and/or offline instructions (cash/check), via
 * email / SMS / both. Also shows the request history for the job (spec §9).
 */
const SendPaymentRequestModal = ({ isOpen, onClose, proposal, onSuccess }) => {
  const { userData } = useContext(AuthContext);
  const canSend = userData?.permissions?.includes('payments');

  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [amount, setAmount] = useState('');
  const [squareMode, setSquareMode] = useState('ach_card'); // ach | ach_card | off
  const [cash, setCash] = useState(true);
  const [check, setCheck] = useState(true);
  const [delivery, setDelivery] = useState('email');
  const [customMessage, setCustomMessage] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const propertyClients = (proposal?.property?.clients || [])
    .map((link) => link.client)
    .filter(Boolean);

  const load = useCallback(async () => {
    if (!proposal?.id) return;
    setLoading(true);
    try {
      const [ledgerRes, requestsRes] = await Promise.all([
        paymentServices.getLedger(proposal.id),
        paymentServices.getRequests(proposal.id),
      ]);
      setBalance(ledgerRes.data.balance);
      setRequests(requestsRes.data || []);
      setAmount(String(ledgerRes.data.balance?.collectible ?? ''));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load payment info');
    } finally {
      setLoading(false);
    }
  }, [proposal?.id]);

  useEffect(() => {
    if (isOpen) {
      load();
      const def =
        propertyClients.find((c) => c.id === proposal?.selectedClientId) || propertyClients[0];
      setSelectedClientId(def?.id || '');
      setEmail(def?.email || '');
      setPhone(def?.phoneNumber || '');
      setCustomMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, proposal?.id]);

  const handleClientChange = (e) => {
    setSelectedClientId(e.target.value);
    const c = propertyClients.find((x) => x.id === e.target.value);
    setEmail(c?.email || '');
    setPhone(c?.phoneNumber || '');
  };

  const squareEnabled = squareMode !== 'off';
  const client = propertyClients.find((c) => c.id === selectedClientId);
  const clientName = client?.fullName || 'Customer';

  const previewText = (() => {
    const amt = fmtMoney(amount);
    const lines = [
      `Hi ${clientName}, thank you for choosing Airmaxx Heating & Cooling.`,
      `Our records show an outstanding balance of ${amt} for job ${proposal?.publicId}.`,
    ];
    if (squareEnabled) lines.push('You can securely pay online using the link below:\n[Square payment link]');
    if (cash || check) {
      const offline = [cash && 'cash', check && 'check'].filter(Boolean).join(' and ');
      lines.push(
        `We also accept ${offline}. Please contact our office to arrange payment or mail checks to 1865 John Towers Ave #B, El Cajon, CA 92020.`,
      );
    }
    if (customMessage) lines.push(customMessage);
    lines.push("If you've already submitted payment, please disregard this message.");
    return lines.join('\n\n');
  })();

  const handleSend = async () => {
    const methods = {
      ach: squareEnabled,
      card: squareMode === 'ach_card',
      cash,
      check,
    };
    if (!methods.ach && !methods.card && !methods.cash && !methods.check) {
      toast.error('Select at least one payment method');
      return;
    }
    setSending(true);
    try {
      await paymentServices.sendRequest(proposal.id, {
        amount: parseFloat(amount),
        methods,
        delivery,
        customMessage: customMessage || undefined,
        clientId: selectedClientId || undefined,
        email: email || undefined,
        phone: phone || undefined,
      });
      toast.success(delivery === 'none' ? 'Payment request recorded' : 'Payment request sent');
      await load();
      onSuccess && onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to send payment request');
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (request) => {
    try {
      await paymentServices.resendRequest(request.id, { email: email || undefined, phone: phone || undefined });
      toast.success('Request re-sent (same payment link)');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to resend');
    }
  };

  const handleCancel = async (request) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Cancel this payment request? Optional reason:', '');
    if (reason === null) return;
    try {
      await paymentServices.cancelRequest(request.id, reason || 'canceled by employee');
      toast.success('Request canceled');
      await load();
      onSuccess && onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to cancel');
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box sx={modal_style}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ bgcolor: '#0d3c61', color: '#fff', p: 1, borderRadius: 1, mr: 2, display: 'flex' }}>
            <RequestQuoteIcon />
          </Box>
          <Box>
            <Typography variant="h6">Send Invoice</Typography>
            <Typography variant="caption" color="text.secondary">
              Job {proposal?.publicId}
            </Typography>
          </Box>
        </Box>

        {loading || !balance ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {/* Balance summary */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: '#f8f9fa', borderRadius: 1, p: 1.5, mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Job Total</Typography>
                <Typography fontWeight="bold">{fmtMoney(balance.contractPrice)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Payments Received</Typography>
                <Typography fontWeight="bold" color="success.main">{fmtMoney(balance.completedTotal)}</Typography>
              </Box>
              {balance.pendingTotal > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Pending (ACH)</Typography>
                  <Typography fontWeight="bold" color="warning.main">{fmtMoney(balance.pendingTotal)}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Outstanding Balance</Typography>
                <Typography fontWeight="bold" color={balance.balance > 0 ? 'error.main' : 'success.main'}>
                  {fmtMoney(balance.balance)}
                </Typography>
              </Box>
            </Box>

            {balance.collectible > 0 && canSend && (
              <>
                {/* Client + contacts */}
                {propertyClients.length > 1 && (
                  <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                    <InputLabel>Client</InputLabel>
                    <Select value={selectedClientId} label="Client" onChange={handleClientChange}>
                      {propertyClients.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.fullName || c.email}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                  <TextField
                    label="Email" size="small" value={email} sx={{ flex: 1, minWidth: 200 }}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <TextField
                    label="Phone (SMS)" size="small" value={phone} sx={{ flex: 1, minWidth: 160 }}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Box>

                {/* Amount */}
                <TextField
                  label="Amount to request" size="small" fullWidth sx={{ mb: 1.5 }}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  helperText={`Defaults to the full outstanding balance (${fmtMoney(balance.collectible)})`}
                />

                {/* Square methods */}
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Online payment (Square)</Typography>
                <RadioGroup row value={squareMode} onChange={(e) => setSquareMode(e.target.value)} sx={{ mb: 1 }}>
                  <FormControlLabel value="ach" control={<Radio size="small" />} label="ACH only" />
                  <FormControlLabel value="ach_card" control={<Radio size="small" />} label="ACH + Credit/Debit Card" />
                  <FormControlLabel value="off" control={<Radio size="small" />} label="Disabled" />
                </RadioGroup>

                {/* Offline methods */}
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Offline payment</Typography>
                <Box sx={{ mb: 1.5 }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={cash} onChange={(e) => setCash(e.target.checked)} />}
                    label="Cash"
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={check} onChange={(e) => setCheck(e.target.checked)} />}
                    label="Check"
                  />
                </Box>

                {/* Delivery */}
                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                  <InputLabel>Delivery</InputLabel>
                  <Select value={delivery} label="Delivery" onChange={(e) => setDelivery(e.target.value)}>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="sms">SMS</MenuItem>
                    <MenuItem value="both">SMS + Email</MenuItem>
                    <MenuItem value="none">Don't send (record only)</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Personal note (optional)" size="small" fullWidth multiline minRows={2} sx={{ mb: 1.5 }}
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                />

                {/* Preview */}
                {delivery !== 'none' && (
                  <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1, p: 1.5, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">Customer message preview</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>{previewText}</Typography>
                  </Box>
                )}

                <Button
                  variant="contained" fullWidth startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                  disabled={sending || !parseFloat(amount)}
                  onClick={handleSend}
                  sx={{ mb: 2 }}
                >
                  {sending ? 'Sending…' : 'Send Payment Request'}
                </Button>
              </>
            )}
            {balance.collectible > 0 && !canSend && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You don't have the Payments permission — ask the office to send the request.
              </Typography>
            )}
            {balance.collectible <= 0 && (
              <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                {balance.pendingTotal > 0
                  ? 'A pending (ACH) payment covers the remaining balance.'
                  : 'This job is fully paid — no payment request needed.'}
              </Typography>
            )}

            {/* History (spec §9) */}
            {requests.length > 0 && (
              <>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Payment Requests</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Methods</TableCell>
                      <TableCell>Delivery</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.sentAt ? new Date(r.sentAt).toLocaleDateString() : '—'}</TableCell>
                        <TableCell>{fmtMoney(r.amount)}</TableCell>
                        <TableCell>{methodsLabel(r.methodsOffered)}</TableCell>
                        <TableCell>{r.delivery === 'none' ? 'manual' : r.delivery}</TableCell>
                        <TableCell>
                          <Tooltip title={r.statusReason || ''}>
                            <Chip size="small" label={r.status.replace('_', ' ')} color={STATUS_COLORS[r.status] || 'default'} />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {r.squarePaymentUrl && ['sent', 'delivery_failed', 'partially_paid'].includes(r.status) && (
                            <Tooltip title="Open payment page">
                              <Button size="small" href={r.squarePaymentUrl} target="_blank" sx={{ minWidth: 0, p: 0.5 }}>
                                <OpenInNewIcon fontSize="small" />
                              </Button>
                            </Tooltip>
                          )}
                          {canSend && ['sent', 'delivery_failed'].includes(r.status) && (
                            <>
                              <Tooltip title="Resend (same link)">
                                <Button size="small" onClick={() => handleResend(r)} sx={{ minWidth: 0, p: 0.5 }}>
                                  <ReplayIcon fontSize="small" />
                                </Button>
                              </Tooltip>
                              <Tooltip title="Cancel request">
                                <Button size="small" color="error" onClick={() => handleCancel(r)} sx={{ minWidth: 0, p: 0.5 }}>
                                  <CancelIcon fontSize="small" />
                                </Button>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button onClick={onClose}>Close</Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SendPaymentRequestModal;
